import { Check } from 'lucide-react';

import { useLanguage } from '@/components/LanguageContext';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import HeroProductoV3 from '@/components/landing/v3/HeroProductoV3';
import { WidgetCarrusel, WidgetResumen } from '@/components/landing/v3/MockWidget';
import PorQueImporta from '@/components/landing/v3/PorQueImporta';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * WidgetResenasV3 — el widget de reseñas para la web del cliente.
 *
 * Portada desde `Widget de reseñas.dc.html`. Todas las opciones que enseña son
 * reales (`validate_theme` en `gbusiness/services/widgets.py`): tema, idioma,
 * color de acento que por defecto cae al de marca, redondeo de 0 a 24 px, y
 * los tres interruptores del carrusel.
 *
 * **Las dos maquetas del widget NO salen del diseño.** Están copiadas de las
 * plantillas que sirve el backend, y viven en `MockWidget.jsx` con el detalle
 * de en qué se había equivocado el `.dc.html`. Es la única imagen que un
 * visitante tiene de lo que va a pegar en su web: si lo que instala no se
 * parece a lo que vio, esta página le prometió otra cosa.
 */
export default function WidgetResenasV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.producto.widgetResenas;
  const comun = raiz.producto.comun;

  return (
    <LandingShellV3>
      <HeroProductoV3
        badge={c.hero.badge}
        titulo={c.hero.titulo}
        acento={c.hero.acento}
        sub={c.hero.sub}
        cta={c.hero.cta}
        ctaVer={c.hero.ctaVer}
        anclaVer="#formatos"
        ctaTrialId="producto.widget.hero.trial"
        ctaVerId="producto.widget.hero.ver"
      />

      <Formatos c={c.formatos} />
      <Personalizar c={c.personalizar} />
      <Dominios c={c.dominios} />

      <PorQueImporta
        kicker={c.porQue.kicker}
        cifra={c.porQue.cifra}
        cifraPie={c.porQue.cifraPie}
        titulo={c.porQue.titulo}
        parrafos={[c.porQue.p1]}
      />

      <FaqAcordeon titulo={c.faqTitulo} preguntas={c.faq} />

      <CtaFinalOscuro
        tituloA={c.cierre.titulo}
        acento={c.cierre.acento}
        subtitulo={c.cierre.sub}
        cta={comun.cierre.cta}
        microcopy={comun.cierre.microcopy}
        enterpriseA={comun.cierre.enterpriseA}
        enterpriseCta={comun.cierre.enterpriseCta}
        ctaTrial="producto.widget.cierre.trial"
        ctaVentas="producto.widget.cierre.ventas"
      />
    </LandingShellV3>
  );
}

function Formatos({ c }) {
  const r = c.resumen;
  const car = c.carrusel;
  return (
    <section id="formatos" className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-6 lg:grid-cols-2 mt-7">
          <article className="bg-white border border-v3-line rounded-[20px] px-7 py-[30px]">
            <span className="inline-flex text-[11.5px] font-bold text-v3-golddeep bg-v3-goldsoft rounded-full px-2.5 py-1.5">
              {r.etiqueta}
            </span>
            <h2 className="text-[22px] md:text-2xl leading-[1.2] tracking-[-.02em] font-bold text-v3-ink mt-3.5 max-w-[22ch]">
              {r.titulo}
            </h2>
            <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3">{r.cuerpo}</p>

            {/* La vista previa va sobre la superficie clara de la landing,
                pero lo de dentro es el widget tal cual: ver `MockWidget`. */}
            <div className="bg-v3-surface rounded-2xl p-6 mt-[22px]">
              <div className="max-w-[300px] mx-auto">
                <WidgetResumen negocio={r.negocio} nota={r.nota} total={r.total} />
              </div>
            </div>
          </article>

          <article className="bg-white border border-v3-line rounded-[20px] px-7 py-[30px] overflow-hidden">
            <span className="inline-flex text-[11.5px] font-bold text-v3-golddeep bg-v3-goldsoft rounded-full px-2.5 py-1.5">
              {car.etiqueta}
            </span>
            <h2 className="text-[22px] md:text-2xl leading-[1.2] tracking-[-.02em] font-bold text-v3-ink mt-3.5 max-w-[22ch]">
              {car.titulo}
            </h2>
            <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3">{car.cuerpo}</p>

            <div className="bg-v3-surface rounded-2xl py-[22px] mt-[22px]">
              <WidgetCarrusel resenas={car.resenas} />
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function Personalizar({ c }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto grid gap-10 lg:grid-cols-2 lg:gap-11 items-center">
        <div>
          <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[24ch]">
            {c.titulo}
          </h2>
          <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[60ch]">{c.cuerpo}</p>
        </div>

        {/* Todo el panel es una ilustración de la UI: ni se pulsa ni se
            arrastra, así que va fuera del árbol de accesibilidad. Lo que hace
            cada control ya lo cuenta el párrafo de al lado. */}
        <div aria-hidden="true" className="bg-v3-surface rounded-[20px] p-6 flex flex-col gap-3.5">
          <div className="bg-white rounded-2xl px-4 py-3.5 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
            <div className="text-[11.5px] font-bold text-v3-faint">{c.temaEtiqueta}</div>
            <div className="flex gap-1.5 bg-v3-sunken rounded-full p-1 mt-2.5">
              {c.temas.map((t, i) => (
                <span
                  key={t}
                  className={`flex-1 text-center text-[12.5px] font-semibold rounded-full py-2 ${
                    i === 0 ? 'bg-white text-v3-ink' : 'text-v3-muted'
                  }`}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl px-4 py-3.5 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
            <div className="text-[11.5px] font-bold text-v3-faint">{c.colorEtiqueta}</div>
            <div className="flex items-center gap-2.5 mt-2.5">
              {['#feae2c', '#111111', '#2563eb', '#16a34a'].map((color, i) => (
                <span
                  key={color}
                  className="w-7 h-7 rounded-full block flex-none"
                  style={{
                    background: color,
                    boxShadow: i === 0 ? '0 0 0 2px #fff, 0 0 0 4px #feae2c' : 'none',
                  }}
                />
              ))}
              <span className="text-xs text-v3-faint ml-1">{c.colorNota}</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl px-4 py-3.5 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
            <div className="flex items-center justify-between gap-2.5">
              <span className="text-[11.5px] font-bold text-v3-faint">{c.redondeoEtiqueta}</span>
              <span className="text-xs font-bold text-v3-golddeep">{c.redondeoValor}</span>
            </div>
            <div className="relative h-1.5 rounded-full bg-v3-track mt-3.5">
              <div className="absolute left-0 w-[58%] inset-y-0 rounded-full bg-v3-gold" />
              <div className="absolute left-[58%] -top-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-v3-gold -translate-x-1/2" />
            </div>
          </div>

          <div className="bg-white rounded-2xl px-4 py-3.5 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
            <div className="text-[11.5px] font-bold text-v3-faint">{c.soloCarrusel}</div>
            <div className="flex flex-col gap-2.5 mt-3">
              {c.opciones.map((o) => (
                <div key={o.clave} className="flex items-center gap-3">
                  <span
                    className={`flex-1 text-[13.5px] ${o.on ? 'text-v3-ink' : 'text-v3-faint'}`}
                  >
                    {o.n}
                  </span>
                  <span
                    className={`w-[34px] h-5 rounded-full p-0.5 flex flex-none ${
                      o.on ? 'bg-v3-gold justify-end' : 'bg-v3-track justify-start'
                    }`}
                  >
                    <span className="w-4 h-4 rounded-full bg-white block shadow-[0_1px_3px_rgba(0,0,0,.2)]" />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Dominios({ c }) {
  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto grid gap-10 lg:grid-cols-2 lg:gap-11 items-center">
        <div>
          <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[24ch]">
            {c.titulo}
          </h2>
          <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[60ch]">{c.cuerpo}</p>
        </div>

        <div aria-hidden="true" className="bg-v3-surface rounded-[20px] p-6">
          <div className="text-[12.5px] font-bold text-v3-muted">{c.etiqueta}</div>
          <div className="bg-white rounded-2xl px-4 py-3.5 mt-3 flex items-center gap-3 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
            <Check className="w-4 h-4 text-v3-green flex-none" strokeWidth={3} />
            <span className="flex-1 font-mono text-[13.5px] font-medium text-v3-ink truncate">
              {c.ejemplo}
            </span>
            <span className="text-xs font-semibold text-v3-green bg-v3-greensoft rounded-full px-2.5 py-1.5 whitespace-nowrap">
              {c.activo}
            </span>
          </div>
          <div className="border border-dashed border-v3-faint rounded-2xl px-4 py-3.5 mt-2.5 flex items-center gap-3">
            <span className="text-[13.5px] text-v3-faint flex-1">{c.anadir}</span>
            <span className="text-[12.5px] font-bold text-v3-golddeep whitespace-nowrap">
              {c.anadirCta}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
